import { logWarn, logInfo } from '@/lib/logger';
/**
 * Microsoft Clarity integration
 *
 * Uses script tag injection (Microsoft's recommended approach) instead of
 * the buggy @microsoft/clarity npm package which throws "a[c] is not a function" errors.
 */

import type { AnalyticsUser } from './types';

type ClarityFunction = (...args: unknown[]) => void;

interface ClarityWindow extends Window {
  clarity?: ClarityFunction & { q?: unknown[][] };
}

let isInitialized = false;

/**
 * Check if Clarity is available on window
 */
function isClarityReady(): boolean {
  return isInitialized && typeof window !== 'undefined' && typeof (window as ClarityWindow).clarity === 'function';
}

/**
 * Get the clarity function from window
 */
function getClarity(): ClarityFunction | undefined {
  return (window as ClarityWindow).clarity;
}

/**
 * Initialize Microsoft Clarity via script tag injection
 * This is more reliable than the npm package
 */
export function init(projectId: string): void {
  if (isInitialized) return;
  if (typeof window === 'undefined') return;

  if (!projectId) {
    logWarn('[Clarity] No project ID provided, skipping initialization');
    return;
  }

  try {
    // Clarity initialization script (from Microsoft's setup guide)
    const w = window as ClarityWindow;
    const clarityQueue: ClarityFunction & { q?: unknown[][] } = function clarityQueue(...args: unknown[]) {
      (w.clarity!.q = w.clarity!.q || []).push(args);
    };
    w.clarity = w.clarity || clarityQueue;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.clarity.ms/tag/' + projectId;
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);

    isInitialized = true;
    logInfo('[Clarity] Initialized with project ID:', projectId);
  } catch (error) {
    logWarn('[Clarity] Failed to initialize:', error);
  }
}

/**
 * Identify user in Clarity for session replay association
 */
export function identify(user: AnalyticsUser): void {
  if (!isClarityReady()) return;

  try {
    // Clarity identify: customUserId, customSessionId (optional), customPageId (optional), friendlyName (optional)
    getClarity()?.('identify', user.userId, undefined, undefined, user.githubLogin || undefined);

    // Set custom tags for filtering sessions
    setTag('subscription_tier', user.subscriptionTier);
    setTag('org_id', user.orgId || 'none');
    setTag('is_trial', user.isTrial ? 'true' : 'false');
    setTag('org_name', user.orgName || '');
    setTag('is_personal', user.isPersonal != null ? String(user.isPersonal) : '');

    if (user.orgSlug) {
      setTag('org_slug', user.orgSlug);
    }
  } catch (error) {
    logWarn('[Clarity] Failed to identify user:', error);
  }
}

/**
 * Set a custom tag for session filtering
 */
export function setTag(key: string, value: string): void {
  if (!isClarityReady()) return;

  try {
    getClarity()?.('set', key, value);
  } catch (error) {
    logWarn('[Clarity] Failed to set tag:', error);
  }
}

/**
 * Track an event as a Clarity tag (for filtering)
 */
export function trackEvent(eventName: string, params?: Record<string, string>): void {
  if (!isClarityReady()) return;

  try {
    // Clarity doesn't have native event tracking, but we can use tags
    getClarity()?.('set', 'last_event', eventName);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        getClarity()?.('set', `event_${key}`, value);
      });
    }
  } catch (error) {
    logWarn('[Clarity] Failed to track event:', error);
  }
}

/**
 * Clear user identification (note: Clarity doesn't have a native reset)
 */
export function reset(): void {
  if (!isClarityReady()) return;

  try {
    // Clear tags - Clarity will still have the session, but tags will be cleared
    getClarity()?.('set', 'subscription_tier', '');
    getClarity()?.('set', 'org_id', '');
    getClarity()?.('set', 'is_trial', '');
    getClarity()?.('set', 'org_slug', '');
    getClarity()?.('set', 'org_name', '');
    getClarity()?.('set', 'is_personal', '');
  } catch (error) {
    logWarn('[Clarity] Failed to reset:', error);
  }
}
